/**
 * Small shared pieces of the Jupiter look: the animated connection dots, status chips, section labels, page headers
 * and the dark video frame.
 */
import { ReactNode } from "react";
import { keyframes } from "@emotion/react";
import { Box, Stack, SxProps, Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { caps, fonts, tones } from "./theme";

const reduced = "@media (prefers-reduced-motion: reduce)";

const travel = keyframes`
  0%, 100% { opacity: 0.22; transform: scale(0.8); }
  18% { opacity: 1; transform: scale(1.25); }
  40% { opacity: 0.22; transform: scale(0.8); }
`;
const breathe = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1.15); }
`;

export type LinkState = "live" | "connecting" | "waiting" | "failed" | "idle";

const linkColor: Record<LinkState, string> = {
  live: tones.live,
  connecting: tones.ochre,
  waiting: "rgba(236,233,228,0.42)",
  failed: tones.fail,
  idle: "rgba(236,233,228,0.22)",
};

/**
 * A line of dots between two endpoints. Live: a cool pulse travels from `from` to `to`. Connecting: a slow warm
 * pulse (still trying). Waiting / idle: dim and static. Failed: red, static. Static everywhere under reduced motion.
 */
export function ConnectionDots({
  state,
  from,
  to,
  label,
  dots = 6,
  size = 4,
  sx,
}: {
  state: LinkState;
  from?: ReactNode;
  to?: ReactNode;
  label?: ReactNode;
  dots?: number;
  size?: number;
  sx?: SxProps<Theme>;
}) {
  const color = linkColor[state];
  const animated = state === "live" || state === "connecting";
  const period = state === "live" ? 1.6 : 2.8;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      role="img"
      aria-label={typeof label === "string" ? label : state}
      sx={{ color, minWidth: 0, ...sx }}
    >
      {from && <Box sx={{ display: "flex", color: state === "idle" ? "text.disabled" : "text.secondary" }}>{from}</Box>}
      <Stack direction="row" alignItems="center" spacing={`${Math.max(3, size)}px`} aria-hidden>
        {Array.from({ length: dots }, (_, i) => (
          <Box
            key={i}
            sx={{
              width: size,
              height: size,
              borderRadius: "50%",
              bgcolor: color,
              opacity: animated ? 0.22 : state === "failed" ? (i % 2 ? 0.35 : 0.9) : 0.6,
              boxShadow: state === "live" ? `0 0 6px ${alpha(tones.live, 0.7)}` : "none",
              animation: animated ? `${travel} ${period}s ease-in-out ${(i * period) / (dots * 2.2)}s infinite` : "none",
              [reduced]: { animation: "none", opacity: animated ? 0.85 : undefined, transform: "none" },
            }}
          />
        ))}
      </Stack>
      {to && <Box sx={{ display: "flex", color: state === "live" ? tones.live : state === "idle" ? "text.disabled" : "text.secondary" }}>{to}</Box>}
      {label && (
        <Typography component="span" variant="caption" sx={{ color: state === "waiting" || state === "idle" ? "text.secondary" : color, pl: 0.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </Typography>
      )}
    </Stack>
  );
}

/** A single status dot; `pulse` breathes it (off under reduced motion). */
export function Dot({ color, pulse, size = 6 }: { color: string; pulse?: boolean; size?: number }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: "inline-block",
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: color,
        boxShadow: `0 0 8px ${alpha(color, 0.6)}`,
        animation: pulse ? `${breathe} 1.8s ease-in-out infinite` : "none",
        [reduced]: { animation: "none" },
      }}
    />
  );
}

type Tone = "warm" | "live" | "ok" | "warn" | "fail" | "neutral";
const toneColor: Record<Tone, string> = { warm: tones.ochre, live: tones.live, ok: tones.ok, warn: tones.warn, fail: tones.fail, neutral: "rgba(236,233,228,0.6)" };

const STATUS: Record<string, { tone: Tone; pulse?: boolean }> = {
  scheduled: { tone: "warm" },
  in_progress: { tone: "live", pulse: true },
  completed: { tone: "ok" },
  cancelled: { tone: "neutral" },
  pending: { tone: "warm", pulse: true },
  approved: { tone: "ok" },
  rejected: { tone: "fail" },
  ready: { tone: "ok" },
  processing: { tone: "neutral", pulse: true },
  failed: { tone: "fail" },
  live: { tone: "live", pulse: true },
  ended: { tone: "neutral" },
  "nothing recorded": { tone: "neutral" },
  revoked: { tone: "neutral" },
  ok: { tone: "ok" },
  allow: { tone: "neutral" },
  deny: { tone: "fail" },
  error: { tone: "fail" },
};

/** The status pill used across the app: a colored dot and a quiet label. Unknown statuses render neutral. */
export function StatusChip({ status, label, tone, pulse }: { status: string; label?: ReactNode; tone?: Tone; pulse?: boolean }) {
  const s = STATUS[status] ?? { tone: "neutral" as Tone };
  const c = toneColor[tone ?? s.tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: 22,
        px: 1,
        borderRadius: "6px",
        border: `1px solid ${alpha(c, 0.32)}`,
        bgcolor: alpha(c, 0.08),
        color: c,
        fontSize: "0.7rem",
        fontWeight: 500,
        letterSpacing: "0.05em",
        whiteSpace: "nowrap",
        lineHeight: 1,
      }}
    >
      <Dot color={c} pulse={pulse ?? s.pulse} />
      {label ?? status.replace(/_/g, " ")}
    </Box>
  );
}

/** Uppercase spaced label over a section, with an optional count and trailing actions. */
export function SectionLabel({ children, count, action, icon }: { children: ReactNode; count?: number; action?: ReactNode; icon?: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 28 }}>
      {icon && <Box sx={{ display: "flex", color: "primary.main" }}>{icon}</Box>}
      <Typography variant="overline" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
        {children}
        {count !== undefined && <Box component="span" sx={{ ml: 1, color: "primary.main" }}>{count}</Box>}
      </Typography>
      <Box sx={{ flexGrow: 1, height: "1px", background: `linear-gradient(90deg, ${tones.line}, transparent)` }} />
      {action}
    </Stack>
  );
}

/** Page title in the landing page's voice: a small overline and a wide-spaced uppercase heading. */
export function PageHeader({ overline, title, children }: { overline?: ReactNode; title: ReactNode; children?: ReactNode }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }} sx={{ pb: 0.5 }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {overline && <Typography variant="overline" sx={{ color: "primary.main", display: "block", lineHeight: 1.6 }}>{overline}</Typography>}
        <Typography variant="h5" component="h1">{title}</Typography>
      </Box>
      {children && <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>{children}</Stack>}
    </Stack>
  );
}

/** Monospace identifiers (accession, AE title, host:port). */
export function Mono({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return <Box component="span" sx={{ fontFamily: fonts.mono, fontSize: "0.85em", letterSpacing: 0, ...sx }}>{children}</Box>;
}

/** A dark rounded frame for video and stills, with a faint warm inner glow drawn over the content. */
export function VideoFrame({ children, aspect, sx, glow = "warm" }: { children: ReactNode; aspect?: string; sx?: SxProps<Theme>; glow?: "warm" | "live" | "none" }) {
  const g = glow === "live" ? tones.live : tones.ochre;
  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "#000",
        borderRadius: "14px",
        overflow: "hidden",
        border: `1px solid ${glow === "live" ? alpha(tones.live, 0.28) : tones.line}`,
        aspectRatio: aspect,
        width: "100%",
        transition: "border-color 0.6s ease",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: glow === "none" ? "inset 0 0 0 1px rgba(255,255,255,0.03)" : `inset 0 0 70px ${alpha(g, 0.07)}, inset 0 0 0 1px rgba(255,255,255,0.03)`,
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** A translucent pill laid over video. */
export function Overlay({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: "rgba(6,7,9,0.62)",
        border: "1px solid rgba(255,255,255,0.1)",
        backdropFilter: "blur(6px)",
        color: tones.text,
        fontSize: "0.72rem",
        maxWidth: "100%",
        minWidth: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export { caps };

/** Shown under every image and video Jupiter displays: Jupiter is not a cleared diagnostic viewer. */
export function NotForDiagnosis({ what = "video" }: { what?: "video" | "image" }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      Reference {what} only. Not for diagnostic use.
    </Typography>
  );
}
